/**
 * Implementation of the Promise/A pattern. An instance of this class
 * represents a future or past event that can either succeed or fail. You can
 * set functions to run when the promise is fulfilled, when the promise fails,
 * or always (regardless of success or failure). Furthermore, if the event has
 * already taken place, attaching a function to a promise will cause that
 * function to execute immediately. Because Foundation.Promise inherits from
 * Foundation.Observable, it can be useful to use as a way to signify that the
 * data contents (accessible via get() and set()) are available.
 *
 * Also see: http://wiki.commonjs.org/wiki/Promises/A
 * @constructor
 * @param {...Foundation.Promise} args
 * @extends Foundation.Observable
 */
Foundation.Promise = function (...args) {
    Foundation.Observable.call(this);

    /**
     * Current state of the promise.
     * @private
     * @type {Foundation.Promise.State}
     */
    this._promiseState = Foundation.Promise.State.INCOMPLETE;

    /**
     * @private
     * @type {Array.<Foundation.Promise>}
     */
    this._promiseChildren = [];

    /**
     * @private
     * @type {number}
     */
    this._promiseTotalChildren = args.length;

    /**
     * @private
     * @type {number}
     */
    this._promiseFulfilledChildren = 0;

    // Take note when child promises are fulfilled.
    for (var i = 0; i < args.length; i++) {
        this._promiseAddChild(args[i]);
    }
};
Foundation.inherit(Foundation.Promise, Foundation.Observable);

/**
 * Create a new promise from an array of promises that will be fulfilled
 * when all the promises in the array are fulfilled, or fail when just
 * one promise in the array fails. This is somewhat different than how
 * the constructor works, in that it is more forgiving if a promise that has
 * already been fulfilled or failed is included in the array.
 * @param {Array.<Foundation.Promise>} promises
 * @param {boolean=} shortCircutOnFail
 * @return {!Foundation.Promise}
 */
Foundation.Promise.fromArray = function (promises, shortCircutOnFail) {
    if (shortCircutOnFail === undefined) {
        shortCircutOnFail = true;
    }

    var result = new Foundation.Promise();
    var failed = false;
    var completedPromises = 0;

    for (var i = 0; i < promises.length; i++) {
        promises[i]
            .always(function () {
                ++completedPromises;
                Foundation.Promise._errorArrayAwareExtend(result, this);
            }.bind(promises[i]))
            .otherwise(function () {
                failed = true;
                if (shortCircutOnFail) {
                    result.fail();
                }
            })
            .always(function () {
                if (completedPromises === promises.length) {
                    if (failed && !shortCircutOnFail) {
                        result.fail();
                    }
                    else if (!failed) {
                        result.fulfill();
                    }
                }
            });
    }

    return result;
};

/**
 * @private
 * @param {Foundation.Promise} a
 * @param {Foundation.Promise} b
 */
Foundation.Promise._errorArrayAwareExtend = function (a, b) {
    var errorArray = /** @type {Array} */ (a.get("errors"));
    a.extend(b);
    if (errorArray) {
        a.set("errors", errorArray.concat(b.get("errors") || []));
    }
};

/**
 * Promise tri-state. INCOMPLETE, FULFILLED, FAILED.
 * @enum {number}
 */
Foundation.Promise.State = {
    INCOMPLETE: 0,
    FULFILLED: 1,
    FAILED: -1
};

/**
 * Current state of the promise.
 * @public
 * @return {Foundation.Promise.State}
 */
Foundation.Promise.prototype.promiseState = function () {
    return this._promiseState;
};

/**
 * Is this promise either fulfilled or failed?
 * @public
 * @return {boolean}
 */
Foundation.Promise.prototype.isComplete = function () {
    return this.promiseState() !== Foundation.Promise.State.INCOMPLETE;
};

/**
 * Is this not yet fulfilled or failed?
 * @public
 * @return {boolean}
 */
Foundation.Promise.prototype.isIncomplete = function () {
    return this.promiseState() === Foundation.Promise.State.INCOMPLETE;
};

/**
 * Has this promise been fulfilled?
 * @public
 * @return {boolean}
 */
Foundation.Promise.prototype.isFulfilled = function () {
    return this.promiseState() === Foundation.Promise.State.FULFILLED;
};

/**
 * Has this promise failed?
 * @public
 * @return {boolean}
 */
Foundation.Promise.prototype.isFailed = function () {
    return this.promiseState() === Foundation.Promise.State.FAILED;
}

/**
 * Add a child promise.
 * @param {Foundation.Promise} promise
 * @private
 */
Foundation.Promise.prototype._promiseAddChild = function (promise) {
    if (this.isComplete()) throw "Attempt to add to a completed promise.";
    this._promiseChildren.push(promise);

    // We have to wait for all children to be fulfilled before we can
    // fulfill ourselves.
    promise.then(this._promiseChildFulfilled.bind(this));

    // On the other hand, if a child fails we must fail immediately.
    promise.otherwise(this.fail.bind(this));
};

/**
 * Fulfills a promise and does callbacks.
 * @return {!Foundation.Promise}
 */
Foundation.Promise.prototype.fulfill = function () {
    if (this.isComplete()) throw "Attempt to fulfill a completed promise.";
    this._promiseState = Foundation.Promise.State.FULFILLED;
    this.fire("promise:fulfilled");
    this._cleanup();
    return this;
};

/**
 * Fails a promise and does callbacks.
 * @return {!Foundation.Promise}
 */
Foundation.Promise.prototype.fail = function () {
    if (this.isComplete()) throw "Attempt to fail a completed promise.";
    this._promiseState = Foundation.Promise.State.FAILED;
    this.fire("promise:failed");
    this._cleanup();
    return this;
};

/**
 * Cleanup after fulfill or fail. Should improve memory usage.
 * @private
 */
Foundation.Promise.prototype._cleanup = function () {
    this.unobserveAll("promise:fulfilled")
        .unobserveAll("promise:failed");
};

/**
 * Assign a callback to when the promise is fulfilled and/or failed.
 * @param {function()?} onFulfill
 * @param {function()?=} onFail
 * @return {!Foundation.Promise}
 */
Foundation.Promise.prototype.then = function (onFulfill, onFail) {
    if (onFulfill) {
        if (this.promiseState() === Foundation.Promise.State.FULFILLED) {
            onFulfill();
        }
        else if (this.isIncomplete()) {
            this.observe("promise:fulfilled", function () {
                onFulfill();
            });
        }
    }

    if (onFail) {
        this.otherwise(onFail);
    }

    return this;
};

/**
 * Assign a callback to when the promise fails.
 * @param {function()} callback
 * @return {!Foundation.Promise}
 */
Foundation.Promise.prototype.otherwise = function (callback) {
    if (this.promiseState() === Foundation.Promise.State.FAILED) {
        callback();
    }
    else if (this.isIncomplete()) {
        this.observe("promise:failed", function () {
            callback();
        });
    }

    return this;
};

/**
 * Assign a callback to when the promise either fails or gets fulfilled.
 * @param {function ()} callback
 * @return {!Foundation.Promise}
 */
Foundation.Promise.prototype.always = function (callback) {
    return this.then(callback).otherwise(callback);
};

/**
 * Make it so that when the passed promise is fulfilled or failed, the same
 * happens to this promise. Often you could just use the Foundation.Promise
 * constructor to create a new promise that acheives this goal, but this is
 * useful when you already have a promise outside your current scope.
 * Additionally, this method will always copy the Foundation.Observable data
 * from the married promise.
 * @param {Foundation.Promise} spouse
 * @return {!Foundation.Promise}
 */
Foundation.Promise.prototype.marry = function (spouse) {
    spouse
        .always(this.extend.bind(this, spouse))
        .then(this.fulfill.bind(this))
        .otherwise(this.fail.bind(this));

    return this;
};

/**
 * Fulfills a promise and does callbacks. If promise is already complete do
 * not throw an exception, instead do nothing. Generally, this method should
 * be avoided but it can be useful in some circumstances.
 * @return {!Foundation.Promise}
 */
Foundation.Promise.prototype.tryFulfill = function () {
    if (this.isIncomplete()) {
        this.fulfill();
    }
    return this;
};

/**
 * Fails a promise and does callbacks. If promise is already complete do
 * not throw an exception, instead do nothing. Generally, this method should
 * be avoided but it can be useful in some circumstances.
 * @return {!Foundation.Promise}
 */
Foundation.Promise.prototype.tryFail = function () {
    if (this.isIncomplete()) {
        this.fail();
    }
    return this;
};

/**
 * Fulfill a promise and extend it with the Object or Foundation.Observable
 * instance.
 * @param {(Object.<string,*>|Foundation.Observable)} obj
 * @return {!Foundation.Promise}
 */
Foundation.Promise.prototype.fulfillWith = function (obj) {
    this.extend(obj);
    return this.fulfill();
};

/**
 * Fail a promise and extend it with the Object or Foundation.Observable
 * instance.
 * @param {(Object.<string,*>|Foundation.Observable)} obj
 * @return {!Foundation.Promise}
 */
Foundation.Promise.prototype.failWith = function (obj) {
    this.extend(obj);
    return this.fail();
};

/**
 * Keep track of child promises getting fulfilled.
 * @private
 */
Foundation.Promise.prototype._promiseChildFulfilled = function () {
    this._promiseFulfilledChildren += 1;
    if (this._promiseFulfilledChildren === this._promiseTotalChildren) this.fulfill();
};

/**
 * @return {Array.<string>}
 */
Foundation.Promise.prototype.errors = function () {
    if (this.isFailed()) {
        return /** @type {Array.<string>} */ (this.get("errors")) || [];
    }
    else {
        return null;
    }
}

// ---------------------------------------------------------------------

/**
 * Convience promise for DOMContentLoaded event.
 * @public
 */
Foundation.documentReady = new Foundation.Promise();
jQuery(document).ready(function () { Foundation.documentReady.fulfill(); });

/**
 * Convience promise for Window load event.
 * @public
 */
Foundation.windowLoaded = new Foundation.Promise();
jQuery(window).load(function () { Foundation.windowLoaded.fulfill(); });

window["Foundation"]["documentReady"] = Foundation.documentReady;
window["Foundation"]["windowLoaded"] = Foundation.windowLoaded;

// ---------------------------------------------------------------------

window["Foundation"]["Promise"] = Foundation.Promise;
window["Foundation"]["Promise"]["fromArray"] = Foundation.Promise.fromArray;
window["Foundation"]["Promise"].prototype["then"] = Foundation.Promise.prototype.then;
window["Foundation"]["Promise"].prototype["otherwise"] = Foundation.Promise.prototype.otherwise;
window["Foundation"]["Promise"].prototype["always"] = Foundation.Promise.prototype.always;
window["Foundation"]["Promise"].prototype["fulfill"] = Foundation.Promise.prototype.fulfill;
window["Foundation"]["Promise"].prototype["fail"] = Foundation.Promise.prototype.fail;
window["Foundation"]["Promise"].prototype["marry"] = Foundation.Promise.prototype.marry;
